import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
    DataTableAction,
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import EditIcons from '@/icons/EditIcons'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Eye } from 'lucide-react'

import Paper from '@/components/Paper'
import { EMPLOYEE_STATUS_MAP } from '../../constants'
import { getEmployeesList } from '../../services'
import { Employee } from '../../types/response'

interface EmployeesTabProps {
    departmentId: number
}

const EmployeesTab = ({ departmentId }: EmployeesTabProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // URL params for pagination
    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            defaults: { sort_by: 'employee_name', page: 1, page_size: 10 },
            filterDefaults: {},
        })

    // Merge department filter into query params
    const apiParams = useMemo(
        () => ({
            ...queryParams,
            filters: btoa(JSON.stringify({ department_id: departmentId })),
        }),
        [queryParams, departmentId]
    )

    // Fetch employees
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES, 'department', departmentId, apiParams],
        queryFn: () => getEmployeesList(apiParams),
        enabled: !!departmentId,
        placeholderData: true,
    })

    const employees = data?.results || []
    const pagination = data?.pagination

    // Columns
    const columns = useMemo(
        () =>
            createColumns<Employee>([
                {
                    key: 'employee_code',
                    title: t('employee_code'),
                    width: 120,
                    headerClassName: 'text-left',
                    sortable: true,
                },
                {
                    key: 'employee_name',
                    title: t('employee_name'),
                    width: 200,
                    headerClassName: 'text-left',
                    sortable: true,
                },
                {
                    key: 'position_name',
                    title: t('position'),
                    width: 180,
                    headerClassName: 'text-left',
                    render: (value) => value || '-',
                },
                {
                    key: 'employee_email_company',
                    title: t('email'),
                    width: 220,
                    headerClassName: 'text-left',
                },
                {
                    key: 'employee_status',
                    title: t('status'),
                    width: 100,
                    headerClassName: 'text-center',
                    sortable: true,
                    cellClassName: 'text-center',
                    render: (value) => {
                        const statusConfig =
                            EMPLOYEE_STATUS_MAP[value as number]
                        return (
                            <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                    statusConfig?.className ||
                                    'bg-gray-100 text-gray-700'
                                }`}
                            >
                                {t(statusConfig?.label || 'unknown')}
                            </span>
                        )
                    },
                },
            ]),
        [t]
    )

    // Actions
    const actions: DataTableAction<Employee>[] = useMemo(
        () => [
            {
                key: 'view',
                label: t('view'),
                icon: <Eye className="w-4 h-4" />,
                onClick: (row) =>
                    navigate(
                        generatePathDatacentral(paths.employeeUpdate, {
                            id: row.employee_id,
                        })
                    ),
            },
            {
                key: 'edit',
                label: t('edit'),
                icon: <EditIcons className="w-4 h-4" />,
                onClick: (row) =>
                    navigate(
                        generatePathDatacentral(paths.employeeUpdate, {
                            id: row.employee_id,
                        })
                    ),
            },
        ],
        [t, navigate]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') => {
            setParams({ sort_by: sortBy, sort_order: sortOrder })
        },
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => {
            setParams({ page }, false)
        },
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => {
            setParams({ page_size: pageSize, page: 1 })
        },
        [setParams]
    )

    const stateComponent = renderState()

    return (
        <Paper className="space-y-2">
            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={employees}
                    rowKey={(row) => row.employee_id}
                    size="small"
                    actions={actions}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_employee'),
                        },
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default EmployeesTab
